import { SchemaForm } from "formvuelate";
import RowText from "@/components/input-formvuelate/RowText.vue";
import RowOption from "@/components/input-formvuelate/RowOption.vue";
import SchemaHeading from "@/components/SchemaHeading.vue";
import { arrayContains } from "@/helpers/logicHelpers";
import type { LeningdeelVariable } from "@/models/LeningdeelVariable";
import { useParameters } from "@/store/useParameters";
import { ValueType } from "@/enums/ValueType";
import type { SchemaType } from "@/models/domain/SchemaType";
import type { FormVariable } from "@/models/domain/FormVariable";
import type { FormSchema } from "@/models/domain/FormSchema";

export function situatieSchema(date: string) {
	return {
		situatie: {
			component: RowOption,
			label: "Situatie",
			options: ["Koop", "Omzetten", "Doorstromen", "Uitbreiding", "Complex"],
			valueType: ValueType.String as const,
		},
		gebruiktBankgarantie: {
			component: RowOption,
			label: "Gebruikt bankgarantie",
			options: ["Ja (berekend)", "Ja (handmatig)", "Nee"],
			condition: (model: { situatie?: string }) => {
				return arrayContains(model.situatie, ["Koop", "Complex"]);
			},
			valueType: ValueType.String as const,
		} as FormVariable,
		useNHG: {
			component: RowOption,
			label: "NHG",
			options: ["Ja", "Nee"],
			valueType: ValueType.String as const,
		},
		nhgInputType: {
			component: RowOption,
			label: "NHG-hoogte bepalen",
			options: { auto: "Berekend", manual: "Handmatig" },
			condition: (model: { useNHG?: string }) => {
				return model.useNHG === "Ja";
			},
			valueType: ValueType.String as const,
		} as FormVariable,
		percentageOvb: {
			component: RowOption,
			label: "%-ovb",
			options: [
				...useParameters(date).ovbPercentages,
				{ label: "Handmatig", value: "manual" },
			],
			condition: (model: { situatie?: string }) => {
				return arrayContains(model.situatie, [
					"Koop",
					"Doorstromen",
					"Complex",
				]);
			},
			valueType: ValueType.String as const,
		} as FormVariable,
	} satisfies SchemaType;
}

export const kostenSchema = {
	aankoopkosten: {
		component: SchemaForm,
		schema: {
			_aankoop: {
				component: SchemaHeading,
				label: "Aankoop",
				valueType: ValueType.None as const,
			},
			koopsom: {
				component: RowText,
				label: "Koopsom",
				condition: (model: {
					situatie?: string;
					aankoopkosten?: { koopsom?: number };
				}) => {
					return (
						arrayContains(model.situatie, ["Koop", "Doorstromen", "Complex"]) ||
						model.aankoopkosten?.koopsom !== undefined
					);
				},
				valueType: ValueType.Currency as const,
			},
			ovb: {
				component: RowText,
				label: "Overdrachtsbelasting",
				readOnly: true,
				condition: (model: {
					situatie?: string;
					percentageOvb?: number | "manual";
				}) => {
					return (
						arrayContains(model.situatie, ["Koop", "Doorstromen", "Complex"]) &&
						model.percentageOvb !== "manual"
					);
				},
				valueType: ValueType.Currency as const,
			} as FormVariable,
			ovbManual: {
				component: RowText,
				label: "Overdrachtsbelasting",
				condition: (model: {
					situatie?: string;
					percentageOvb?: number | "manual";
				}) => {
					return (
						arrayContains(model.situatie, ["Koop", "Doorstromen", "Complex"]) &&
						model.percentageOvb === "manual"
					);
				},
				valueType: ValueType.Currency as const,
			} as FormVariable,
			bouwrente: {
				component: RowText,
				label: "Bouwrente",
				valueType: ValueType.Currency as const,
			},
			bankgarantieManual: {
				component: RowText,
				label: "Premie bankgarantie",
				condition: (model: { gebruiktBankgarantie?: string }) => {
					switch (model.gebruiktBankgarantie) {
						case "Ja (handmatig)":
							return true;
						default:
							return false;
					}
				},
				valueType: ValueType.Currency as const,
			},
			bankgarantie: {
				component: RowText,
				readOnly: true,
				label: "Premie bankgarantie",
				condition: (model: { gebruiktBankgarantie?: string }) => {
					switch (model.gebruiktBankgarantie) {
						case "Ja (berekend)":
							return true;
						default:
							return false;
					}
				},
				valueType: ValueType.Currency as const,
			},
			notarisLevering: {
				component: RowText,
				label: "Notaris levering",
				valueType: ValueType.Currency as const,
			},
			makelaarAankoopInclTaxatie: {
				component: RowText,
				label: "Makelaar aankoop inclusief taxatie",
				valueType: ValueType.Currency as const,
			},
			_verbouwing: {
				component: SchemaHeading,
				label: "Verbouwing",
				valueType: ValueType.None as const,
			},
			verbouwing: {
				component: RowText,
				label: "Verbouwing (meerwerk)",
				valueType: ValueType.Currency as const,
			},
			_persoonlijk: {
				component: SchemaHeading,
				label: "Persoonlijk",
				valueType: ValueType.None as const,
			},
			kredietInlossen: {
				component: RowText,
				label: "Krediet inlossen",
				valueType: ValueType.Currency as const,
			},
			box3Opname: {
				component: RowText,
				label: "Box 3 opname",
				valueType: ValueType.Currency as const,
			},
			_bestaandOversluiten: {
				component: SchemaHeading,
				label: "Huidige hypotheek oversluiten",
				valueType: ValueType.None as const,
			},
			hypotheekInlossen: {
				component: RowText,
				label: "Hypotheek inlossen",
				valueType: ValueType.Currency as const,
			},
			boeterente: {
				component: RowText,
				label: "Boeterente",
				condition: (model: { situatie?: string }) => {
					return arrayContains(model.situatie, ["Omzetten", "Complex"]);
				},
				valueType: ValueType.Currency as const,
			},
			_nieuweHypotheek: {
				component: SchemaHeading,
				label: "Nieuwe hypotheek",
				valueType: ValueType.None as const,
			},
			nhgHoogte: {
				component: RowText,
				label: "NHG-premie",
				readOnly: true,
				condition: (model: { useNHG?: string; nhgInputType?: string }) => {
					return model.useNHG === "Ja" && model.nhgInputType === "auto";
				},
				valueType: ValueType.Currency as const,
			} as FormVariable,
			nhgHoogteManual: {
				component: RowText,
				label: "NHG-premie",
				condition: (model: { useNHG?: string; nhgInputType?: string }) => {
					return model.useNHG === "Ja" && model.nhgInputType === "manual";
				},
				valueType: ValueType.Currency as const,
			} as FormVariable,
			taxatiekosten: {
				component: RowText,
				label: "Taxatiekosten",
				valueType: ValueType.Currency as const,
			},
			afsluitkostenGeldverstrekker: {
				component: RowText,
				label: "Afsluitkosten geldverstrekker",
				valueType: ValueType.Currency as const,
			},
			notarisHypotheek: {
				component: RowText,
				label: "Notaris hypotheek",
				valueType: ValueType.Currency as const,
			},
			kostenHypotheekadvies: {
				component: RowText,
				label: "Kosten hypotheekadvies",
				valueType: ValueType.Currency as const,
			},
			tbvBox3Lening: {
				component: RowText,
				label: "Hypotheek anders dan voor eigen woning",
				valueType: ValueType.Currency as const,
			},
			_scheiden: {
				component: SchemaHeading,
				label: "Scheiden",
				valueType: ValueType.None as const,
			},
			uitkoopExPartner: {
				component: RowText,
				label: "Hypotheek voor uitkoop ex-partner",
				valueType: ValueType.Currency as const,
			},
		} satisfies FormSchema,
	},
} satisfies SchemaType;

export function middelenSchema(eigenMiddelenReadOnly: boolean) {
	return {
		benodigdeFinanciering: {
			component: SchemaForm,
			schema: {
				eigenMiddelen: {
					component: RowText,
					label: "Eigen middelen",
					readOnly: eigenMiddelenReadOnly,
					valueType: ValueType.Currency as const,
				},
				lening: {
					component: RowText,
					label: "Lening (bv. ouders)",
					valueType: ValueType.Currency as const,
				},
				starterslening: {
					component: RowText,
					label: "Starterslening",
					valueType: ValueType.Currency as const,
				},
				overbruggingskrediet: {
					component: RowText,
					label: "Overbruggingskrediet",
					valueType: ValueType.Currency as const,
				},
			},
		},
	} satisfies SchemaType;
}

export const financieringsSchema = {
	financiering: {
		component: SchemaForm,
		schema: {
			financieringsresultaat: {
				component: RowText,
				label: "Financieringsresultaat",
				readOnly: true,
				class: "neutral",
				valueType: ValueType.Currency as const,
			},
		},
	},
} satisfies SchemaType;

export const leningdelenSchema = {
	leningdelen: {
		component: SchemaForm,
		schema: {} as Record<string, LeningdeelVariable>,
	},
} satisfies SchemaType;

export const resultaatSchema = {
	resultaat: {
		component: SchemaForm,
		schema: {
			kosten: {
				component: RowText,
				label: "Kosten",
				readOnly: true,
				valueType: ValueType.Currency as const,
			},
			leningdelen: {
				component: RowText,
				label: "Leningdelen",
				readOnly: true,
				valueType: ValueType.Currency as const,
			},
			middelen: {
				component: RowText,
				label: "Middelen",
				readOnly: true,
				valueType: ValueType.Currency as const,
			},
		},
	},
} satisfies SchemaType;

export const schema = {
	totaalNodig: {
		component: RowText,
		label: "Totaal nodig",
		readOnly: true,
		valueType: ValueType.Currency as const,
	},
	benodigdeFinancieringResultaat: {
		component: RowText,
		label: "Benodigde financiering",
		readOnly: true,
		valueType: ValueType.Currency as const,
	},
} satisfies SchemaType;
